import React, { useEffect, useState } from "react";
import { apiUrl } from "./LoginSignup";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Alert,
} from "@mui/material";
import { Add, Delete, Edit, Link as LinkIcon } from "@mui/icons-material";
import axios from "axios";
import Loader from "./Loader";

const BDM_ROLES = ["bdm", "admin"];
const ASSIGN_ROLES = ["srdev", "senior admin", "dev"];

const PaymentLinkManagement = () => {
  const userSession = JSON.parse(localStorage.getItem("userSession")) || {};
  const isBdm = BDM_ROLES.includes(userSession?.user_role);
  const canAssign = ASSIGN_ROLES.includes(userSession?.user_role);
  const isSrdev = userSession?.user_role === "srdev";

  const [paymentLinks, setPaymentLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [users, setUsers] = useState([]);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    clientName: "",
    clientMobile: "",
    amount: "",
    bdmId: "",
    createdBy: "",
    createdByName: "",
    status: "created",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const authHeaders = { Authorization: `${userSession.token}` };

  useEffect(() => {
    fetchPaymentLinks();
    if (canAssign || isSrdev) {
      fetchUsers();
    }
  }, []);

  const fetchPaymentLinks = async () => {
    try {
      const res = await axios.get(`${apiUrl}/payment-links`, {
        headers: authHeaders,
      });
      setPaymentLinks(res.data);
    } catch (err) {
      console.error("Error fetching payment links:", err);
      setError("Failed to fetch payment links");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${apiUrl}/user/bdm-list`, {
        headers: authHeaders,
      });
      setUsers(res.data);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const resetForm = () => {
    setFormData({
      clientName: "",
      clientMobile: "",
      amount: "",
      bdmId: "",
      createdBy: userSession.user_id || "",
      createdByName: userSession.name || "",
      status: "created",
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setOpenDialog(true);
  };

  const openEdit = (link) => {
    setEditingLink(link);
    setFormData({
      clientName: link.clientName || "",
      clientMobile: link.clientMobile || "",
      amount: link.amount || "",
      bdmId: link.bdmId || "",
      createdBy: link.createdBy || "",
      createdByName: link.createdByName || "",
      status: link.status || "created",
    });
    setOpenEditDialog(true);
  };

  const handleCreatePaymentLink = async () => {
    try {
      setError("");
      setSuccess("");

      const payload = {
        clientName: formData.clientName,
        clientMobile: formData.clientMobile,
        amount: parseFloat(formData.amount),
      };

      if (canAssign) {
        payload.bdmId = formData.bdmId;
      }

      await axios.post(`${apiUrl}/payment-links`, payload, {
        headers: authHeaders,
      });

      setSuccess("Payment link created successfully!");
      setOpenDialog(false);
      resetForm();
      fetchPaymentLinks();
    } catch (err) {
      console.error("Error creating payment link:", err);
      setError(err.response?.data?.message || "Failed to create payment link");
    }
  };

  const handleUpdatePaymentLink = async () => {
    try {
      setError("");
      setSuccess("");

      await axios.patch(
        `${apiUrl}/payment-links/${editingLink._id}`,
        {
          clientName: formData.clientName,
          clientMobile: formData.clientMobile,
          amount: parseFloat(formData.amount),
          bdmId: formData.bdmId,
          createdBy: formData.createdBy,
          status: formData.status,
        },
        { headers: authHeaders }
      );

      setSuccess("Payment link updated successfully!");
      setOpenEditDialog(false);
      setEditingLink(null);
      resetForm();
      fetchPaymentLinks();
    } catch (err) {
      console.error("Error updating payment link:", err);
      setError(err.response?.data?.message || "Failed to update payment link");
    }
  };

  const handleDeletePaymentLink = async (id) => {
    if (!window.confirm("Are you sure you want to delete this payment link?")) {
      return;
    }

    try {
      await axios.delete(`${apiUrl}/payment-links/${id}`, {
        headers: authHeaders,
      });
      setSuccess("Payment link deleted successfully!");
      fetchPaymentLinks();
    } catch (err) {
      console.error("Error deleting payment link:", err);
      setError(err.response?.data?.message || "Failed to delete payment link");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess("Payment link copied to clipboard!");
    setTimeout(() => setSuccess(""), 3000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "success";
      case "failed":
        return "error";
      default:
        return "warning";
    }
  };

  const renderUserFields = (isCreate = false) => (
    <>
      <TextField
        fullWidth
        label="Created By"
        margin="normal"
        value={
          isCreate
            ? userSession.name || ""
            : formData.createdByName ||
              users.find((u) => u._id === formData.createdBy)?.name ||
              ""
        }
        InputProps={{ readOnly: true }}
      />

      {isCreate && isBdm && (
        <TextField
          fullWidth
          label="Assigned To"
          margin="normal"
          value={userSession.name || ""}
          InputProps={{ readOnly: true }}
        />
      )}

      {isCreate && canAssign && (
        <FormControl fullWidth margin="normal">
          <InputLabel>Assigned To (BDM)</InputLabel>
          <Select
            value={formData.bdmId}
            label="Assigned To (BDM)"
            onChange={(e) =>
              setFormData({ ...formData, bdmId: e.target.value })
            }
          >
            {users.map((user) => (
              <MenuItem key={user._id} value={user._id}>
                {user.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {!isCreate && isSrdev && (
        <>
          <FormControl fullWidth margin="normal">
            <InputLabel>Assigned To (BDM)</InputLabel>
            <Select
              value={formData.bdmId}
              label="Assigned To (BDM)"
              onChange={(e) =>
                setFormData({ ...formData, bdmId: e.target.value })
              }
            >
              {users.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Created By (User)</InputLabel>
            <Select
              value={formData.createdBy}
              label="Created By (User)"
              onChange={(e) =>
                setFormData({ ...formData, createdBy: e.target.value })
              }
            >
              {users.map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              label="Status"
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value })
              }
            >
              <MenuItem value="created">Created</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
        </>
      )}
    </>
  );

  if (loading) {
    return <Loader />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Payment Link Management
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="h6">Payment Links</Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={openCreateDialog}
                >
                  Create Payment Link
                </Button>
              </Box>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Client Name</TableCell>
                      <TableCell>Mobile</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Assigned To</TableCell>
                      <TableCell>Created By</TableCell>
                      <TableCell>Payment Link</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell>Paid At</TableCell>
                      {isSrdev && <TableCell>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentLinks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isSrdev ? 10 : 9} align="center">
                          No payment links found
                        </TableCell>
                      </TableRow>
                    ) : (
                      paymentLinks.map((link) => (
                        <TableRow key={link._id}>
                          <TableCell>{link.clientName}</TableCell>
                          <TableCell>{link.clientMobile}</TableCell>
                          <TableCell>₹{link.amount.toLocaleString()}</TableCell>
                          <TableCell>{link.bdmName || "-"}</TableCell>
                          <TableCell>{link.createdByName || "-"}</TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(link.paymentLink)}
                              title="Copy link"
                            >
                              <LinkIcon />
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={link.status}
                              color={getStatusColor(link.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(link.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {link.paidAt
                              ? new Date(link.paidAt).toLocaleDateString()
                              : "-"}
                          </TableCell>
                          {isSrdev && (
                            <TableCell>
                              <IconButton
                                color="primary"
                                onClick={() => openEdit(link)}
                                title="Edit"
                              >
                                <Edit />
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={() => handleDeletePaymentLink(link._id)}
                                title="Delete"
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Payment Link</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Client Name"
            margin="normal"
            value={formData.clientName}
            onChange={(e) =>
              setFormData({ ...formData, clientName: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Client Mobile"
            margin="normal"
            value={formData.clientMobile}
            onChange={(e) =>
              setFormData({ ...formData, clientMobile: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Amount (₹)"
            type="number"
            margin="normal"
            value={formData.amount}
            onChange={(e) =>
              setFormData({ ...formData, amount: e.target.value })
            }
          />
          {renderUserFields(true)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleCreatePaymentLink} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Payment Link</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Client Name"
            margin="normal"
            value={formData.clientName}
            onChange={(e) =>
              setFormData({ ...formData, clientName: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Client Mobile"
            margin="normal"
            value={formData.clientMobile}
            onChange={(e) =>
              setFormData({ ...formData, clientMobile: e.target.value })
            }
          />
          <TextField
            fullWidth
            label="Amount (₹)"
            type="number"
            margin="normal"
            value={formData.amount}
            onChange={(e) =>
              setFormData({ ...formData, amount: e.target.value })
            }
          />
          {renderUserFields(false)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdatePaymentLink} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentLinkManagement;
